// src/belediye/components/tasks/GorevSatiri.js
// FAZ-59 — Triyaj listesinin SATIRI.
//
// ESKİDEN satırda 10 işaret vardı, dördü renkli ve üçü aynı şeyi söylüyordu.
// FAZ-57'de 6'ya indirildi; FAZ-59'da satır "çok yavan" bulunduğu için ölçülü
// biçimde zenginleştirildi — YENİ RENK EKLENMEDEN:
//   • "İşe başlandı" / "Henüz başlanmadı" — görevi veren, karşı tarafın
//     başlayıp başlamadığını satırdan görebilmeliydi (kullanıcı isteği).
//   • Onay bekleyende "3 gündür onayda" — beklemenin YAŞI görünür oldu
//     (şeritteki "en uzun bekleyen üstte" sıralamasının görünen karşılığı).
//   • Kapanan/iptalde "5 gün önce kapandı" — kuru "kapandı" yerine.
//   • Satır sonunda "6 gün önce verildi" — işin yaşı.
// TEK renk ekseni korunur: renk yalnız ACİLİYET anlatır (bant rengi), her şey
// nötr metinle söylenir.
//
// Erişilebilirlik: başlık GERÇEK bağlantıdır; aksiyonlar ayrı düğme olarak durur.

import React from "react";
import { Link } from "react-router-dom";
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";

import { statusLabel } from "./GorevBits";
import "./gorev.css";

// Bir ISO damgasının kaç gün öncesi olduğu (negatif olmaz).
export function gunOnce(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

// Termin/durum metni — TEK kalıp. Kapanmış ve onay bekleyen görevlerde termin
// yerine BEKLEME YAŞI yazar; o hâllerde termin zaten anlamını yitirmiştir.
export function terminMetni(task) {
  if (task?.status === "tamamlandi") {
    const n = gunOnce(task.completed_at);
    if (n === null) return "kapandı";
    return n === 0 ? "bugün kapandı" : `${n} gün önce kapandı`;
  }
  if (task?.status === "iptal_edildi") {
    const n = gunOnce(task.cancelled_at);
    if (n === null) return "iptal edildi";
    return n === 0 ? "bugün iptal edildi" : `${n} gün önce iptal edildi`;
  }
  if (task?.status === "onay_bekliyor") {
    const n = gunOnce(task.updated_at);
    if (n === null) return "onayda";
    return n === 0 ? "bugün onaya geldi" : `${n} gündür onayda`;
  }
  if (!task?.due_date) return "";
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  const termin = new Date(`${task.due_date}T00:00:00`);
  const gun = Math.round((termin - bugun) / 86400000);
  if (gun < 0) return `${Math.abs(gun)} gün gecikti`;
  if (gun === 0) return "bugün son";
  if (gun === 1) return "yarın son";
  return `${gun} gün var`;
}

// Mutlak tarih — göreli metnin başlığında (hover) gösterilir.
// Kısaltma yapılmaz: kısaltılmış tarihler erişilebilirlik sorunu yaratıyor.
const AYLAR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export function tamTarih(iso, saat) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const metin = `${d.getDate()} ${AYLAR[d.getMonth()]} ${d.getFullYear()}`;
  return saat ? `${metin}, ${String(saat).slice(0, 5)}` : metin;
}

// "İşe başlandı mı?" — görevi verenin satırdan görmek istediği tek şey.
// Kapanmış/onaydaki görevde sorulmaz (cevabı zaten belli).
export function BaslangicDurumu({ task }) {
  if (task?.status === "devam_ediyor") {
    return (
      <span className="gk-satir-basladi" title="Sorumlu işe başladığını bildirdi">
        <CheckCircleIcon className="gk-ic" aria-hidden="true" />
        İşe başlandı
      </span>
    );
  }
  if (task?.status === "yeni") {
    return <span className="gk-satir-durum">Henüz başlanmadı</span>;
  }
  return <span className="gk-satir-durum">{statusLabel(task)}</span>;
}

export function Taraflar({ task, tarafGoster }) {
  // Görev Ata sayfasında "kime verdim", Görevlerim'de "kim verdi" sorusu
  // yanıtlanır. Görevi verenin adı bugüne kadar satırda HİÇ görünmüyordu —
  // arama kutusu "görevi veren kişi" dediği hâlde.
  if (tarafGoster === "veren") {
    return task.created_by_display ? (
      <span className="gk-kim">{task.created_by_display}</span>
    ) : null;
  }
  const liste = Array.isArray(task.assignees) ? task.assignees : [];
  if (!liste.length) return null;
  const ilk = liste[0];
  const kalan = liste.length - 1;
  return (
    <span className="gk-kim">
      {ilk.display}
      {kalan > 0 && <span className="gk-kim-artik"> +{kalan}</span>}
    </span>
  );
}

export default function GorevSatiri({
  task,
  tarafGoster = "atanan",
  aksiyon = null,
}) {
  const bant = task.band || "terminsiz";
  const zaman = terminMetni(task);
  const oncelikli = task.priority === "yuksek" || task.priority === "kritik";
  const kapali = task.status === "tamamlandi" || task.status === "iptal_edildi";
  const yas = kapali ? null : gunOnce(task.created_at);

  return (
    <li className={`gk-satir gk-satir--${bant}`}>
      <div className="gk-satir-ust">
        <Link className="gk-satir-baslik" to={`/yonetim/gorevler/${task.id}`}>
          {task.title}
        </Link>
        {zaman && (
          <time
            className="gk-satir-zaman"
            dateTime={task.due_date || undefined}
            title={
              task.due_date
                ? tamTarih(task.due_date, task.due_time)
                : "Teslim tarihi girilmemiş"
            }
          >
            {zaman}
          </time>
        )}
      </div>

      <div className="gk-satir-alt">
        {/* Renk tek başına anlam taşımaz: noktanın yanında statü YAZISI var. */}
        <span className="gk-satir-nokta" aria-hidden="true" />
        <BaslangicDurumu task={task} />

        <Taraflar task={task} tarafGoster={tarafGoster} />

        {task.unit_name && <span className="gk-satir-birim">{task.unit_name}</span>}

        {/* Aciliyet YALNIZ yüksek/kritikte görünür — "Normal" bilgi taşımıyor
            ve rengi yok: kırmızı/kehribar aciliyet renkleri termin sinyaliyle
            çakışıyordu. */}
        {oncelikli && (
          <span className="gk-satir-oncelik">{task.priority_display}</span>
        )}

        {task.application && (
          <span className="gk-satir-etiket" title="Bir vatandaş başvurusundan doğdu">
            Başvuru
          </span>
        )}

        {task.comment_count > 0 && (
          <span className="gk-satir-sayi" title={`${task.comment_count} mesaj`}>
            <ChatBubbleLeftRightIcon className="gk-ic" />
            {task.comment_count}
          </span>
        )}
        {task.attachment_count > 0 && (
          <span className="gk-satir-sayi" title={`${task.attachment_count} ek`}>
            <PaperClipIcon className="gk-ic" />
            {task.attachment_count}
          </span>
        )}

        {/* İşin yaşı — satırın sağ ucunda, en soluk bilgi. */}
        {yas !== null && (
          <span className="gk-satir-yas">
            {yas === 0 ? "bugün verildi" : `${yas} gün önce verildi`}
          </span>
        )}
      </div>

      {aksiyon ? <div className="gk-satir-aksiyon">{aksiyon}</div> : null}
    </li>
  );
}
